import { observer } from "mobx-react-lite";
import { Link, useParams } from "react-router-dom";
import { AdjustmentsIcon } from "@heroicons/react/outline";

import { CustomParameters } from "./ParameterControl/CustomParameters";
import { Parameter } from "./ParameterControl";

import { useParameters } from "@/components/ModelParameters/store";
import { ParameterStore } from "@/components/ModelParameters/store/parameter-store";

interface GroupedParameters {
  defaultParameters: ParameterStore[];
  customParameters: ParameterStore[];
}

export const AdvancedControls = observer(() => {
  const store = useParameters();
  const { workspaceId } = useParams();
  const { modelName, models } = store;
  const model = models.find(m => m.llm_model_name === modelName || m.display_name === modelName);
  const isCustomModel = model?.workspace_id;

  const params = store.parameters.reduce(
    (acc: GroupedParameters, parameter: ParameterStore) => {
      if (!parameter.isCustom) {
        acc.defaultParameters.push(parameter);
      } else {
        acc.customParameters.push(parameter);
      }
      return acc;
    },
    { defaultParameters: [], customParameters: [] },
  );

  const hasParameters = params.defaultParameters.length > 0 || params.customParameters.length > 0;

  if (!hasParameters) {
    return (
      <div className="mb-6 flex max-h-[55vh] w-full min-w-[468px] items-center justify-center overflow-y-auto border-t border-gray-200 p-8 pt-12 transition-all duration-300 ease-in-out">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
            <AdjustmentsIcon className="h-6 w-6 text-gray-400" />
          </div>
          <h3 className="mb-2 text-sm font-medium text-gray-900">No parameters created yet</h3>
          {isCustomModel ? (
            <p className="mb-4 text-sm text-gray-500">
              Go to{" "}
              <Link
                to={`/workspace/${workspaceId}/settings/custom-providers-and-models`}
                className="font-medium text-blue-600 hover:text-blue-500 hover:underline"
              >
                Settings
              </Link>{" "}
              to add custom parameters
            </p>
          ) : (
            <p className="mb-4 text-sm text-gray-500">
              This model does not support any parameters.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 grid max-h-[55vh] w-full min-w-[468px] grid-cols-[repeat(2,minmax(0,234px))] gap-6 overflow-y-auto border-t border-gray-200 pt-6 text-gray-500 transition-all duration-300 ease-in-out">
      {params.defaultParameters.map((parameter: ParameterStore) => (
        <div className="w-full" key={parameter.paramId}>
          <Parameter parameter={parameter} onUpdate={() => store.updateExternalData()} />
        </div>
      ))}
      <CustomParameters
        customParams={params.customParameters}
        onUpdate={() => store.updateExternalData()}
      />
    </div>
  );
});

export * from "./types";
export * from "./constants";
